import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, HelpCircle, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const inp =
  "w-full rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-xs text-zinc-100 focus:border-blue-500 focus:outline-none";
const lbl = "block text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-1";

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "question";

export const ApplyQuestions = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/apply-questions");
      setItems(data.questions || []);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (i, k, v) =>
    setItems(items.map((q, idx) => (idx === i ? { ...q, [k]: v } : q)));

  const move = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= items.length) return;
    const copy = [...items];
    [copy[i], copy[to]] = [copy[to], copy[i]];
    setItems(copy);
  };

  const add = () =>
    setItems([
      ...items,
      { id: `q_${items.length + 1}`, label: "", type: "text", options: [], placeholder: "", required: true },
    ]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = items.map((q) => ({
        ...q,
        id: (q.id || slug(q.label)).trim(),
        options: Array.isArray(q.options)
          ? q.options
          : String(q.options || "")
              .split("|")
              .map((o) => o.trim())
              .filter(Boolean),
      }));
      const { data } = await api.put("/admin/apply-questions", { questions: payload });
      setItems(data.questions);
      toast.success("Application questions saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-4 space-y-4">
      <div className="flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-blue-500" />
        <h2 className="text-sm font-heading font-semibold tracking-tight">
          Application questions
        </h2>
        <span className="font-mono text-[10px] text-zinc-600">
          step 1 (discipline + phone) is fixed · these fill the remaining steps, 3 per step
        </span>
      </div>

      <div className="space-y-3">
        {items.map((q, i) => (
          <div
            key={i}
            data-testid={`question-row-${i}`}
            className="border border-zinc-900 bg-black/30 rounded-sm p-3 grid gap-2 lg:grid-cols-[1.4fr_0.8fr_0.7fr_1.4fr_auto]"
          >
            <div>
              <span className={lbl}>Question</span>
              <input
                data-testid={`question-label-${i}`}
                className={inp}
                value={q.label}
                onChange={(e) => patch(i, "label", e.target.value)}
                placeholder="Years trading"
              />
            </div>
            <div>
              <span className={lbl}>Field id</span>
              <input
                data-testid={`question-id-${i}`}
                className={inp}
                value={q.id}
                onChange={(e) => patch(i, "id", e.target.value)}
                placeholder="experience"
              />
            </div>
            <div>
              <span className={lbl}>Type</span>
              <select
                data-testid={`question-type-${i}`}
                className={inp}
                value={q.type}
                onChange={(e) => patch(i, "type", e.target.value)}
              >
                <option value="text">Short text</option>
                <option value="textarea">Long text</option>
                <option value="choice">Choices</option>
              </select>
            </div>
            <div>
              <span className={lbl}>
                {q.type === "choice" ? "Options (a | b | c)" : "Placeholder"}
              </span>
              <input
                data-testid={`question-extra-${i}`}
                className={inp}
                value={
                  q.type === "choice"
                    ? Array.isArray(q.options)
                      ? q.options.join(" | ")
                      : q.options
                    : q.placeholder || ""
                }
                onChange={(e) =>
                  q.type === "choice"
                    ? patch(
                        i,
                        "options",
                        e.target.value.split("|").map((o) => o.trim()).filter(Boolean)
                      )
                    : patch(i, "placeholder", e.target.value)
                }
                placeholder={q.type === "choice" ? "< 1 year | 1 – 3 years" : "Optional hint"}
              />
            </div>
            <div className="flex items-end gap-1.5">
              <label className="flex items-center gap-1.5 font-mono text-[10px] text-zinc-500 mr-1">
                <input
                  type="checkbox"
                  data-testid={`question-required-${i}`}
                  checked={q.required}
                  onChange={(e) => patch(i, "required", e.target.checked)}
                  className="accent-blue-500"
                />
                req
              </label>
              <button
                onClick={() => move(i, -1)}
                data-testid={`question-up-${i}`}
                className="p-1.5 text-zinc-600 hover:text-zinc-200 transition-colors duration-200"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => move(i, 1)}
                data-testid={`question-down-${i}`}
                className="p-1.5 text-zinc-600 hover:text-zinc-200 transition-colors duration-200"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                data-testid={`question-delete-${i}`}
                className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors duration-200"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={add}
          data-testid="add-question-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-800 hover:border-zinc-600 text-zinc-300 text-xs uppercase tracking-[0.1em] font-semibold rounded-sm transition-colors duration-200"
        >
          <Plus className="h-3.5 w-3.5" /> Add question
        </button>
        <div className="flex-1" />
        <button
          onClick={save}
          disabled={busy}
          data-testid="save-questions-btn"
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs uppercase tracking-[0.1em] font-semibold rounded-sm transition-colors duration-200"
        >
          Save questions
        </button>
      </div>
    </div>
  );
};
